import { useEffect, useRef, useState, type FocusEvent } from "react";
import { ClockTime, TimeValue } from "../clockTime";
import { FieldShell } from "./fields";

interface TimeDraft {
  hour: string;
  minute: string;
}

function draftFromValue(value: string): TimeDraft {
  const parsed = TimeValue.parse(value);
  return { hour: parsed.hour, minute: parsed.minute };
}

interface Props {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  value: string;
  onChange: (value: string) => void;
}

export function TimeInput({ id, label, required, error, value, onChange }: Props) {
  const [draft, setDraft] = useState<TimeDraft>(() => draftFromValue(value));
  const hourRef = useRef<HTMLInputElement>(null);
  const minuteRef = useRef<HTMLInputElement>(null);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setDraft(draftFromValue(value));
  }, [value]);

  const leaveGroup = (event: FocusEvent<HTMLInputElement>) => {
    const root = event.currentTarget.closest(".nv-time-input");
    const next = event.relatedTarget;
    if (root && next instanceof Node && root.contains(next)) return;
    focused.current = false;
    setDraft(draftFromValue(value));
  };

  const emit = (next: TimeDraft) => {
    setDraft(next);
    onChange(new TimeValue(next.hour, next.minute).toStored());
  };

  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      error={error}
      className="nv-time-field"
    >
      <div className="nv-time-input" role="group" aria-label={label}>
        <input
          ref={hourRef}
          id={id}
          type="text"
          className="nv-time-hour"
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          placeholder="hh"
          aria-label={`${label} hour`}
          aria-invalid={error ? true : undefined}
          aria-required={required || undefined}
          aria-describedby={error ? `${id}-err` : undefined}
          value={draft.hour}
          onFocus={() => {
            focused.current = true;
          }}
          onBlur={leaveGroup}
          onPaste={(e) => {
            const parsed = ClockTime.parse(e.clipboardData.getData("text").trim());
            if (!parsed) return;
            e.preventDefault();
            emit(draftFromValue(parsed.toValue()));
            minuteRef.current?.focus();
            minuteRef.current?.select();
          }}
          onChange={(e) => {
            const hour = ClockTime.sanitizeHour(e.target.value);
            emit({ ...draft, hour });
            if (ClockTime.hourReadyToAdvance(hour)) {
              minuteRef.current?.focus();
              minuteRef.current?.select();
            }
          }}
        />
        <span className="nv-time-sep" aria-hidden="true">
          :
        </span>
        <input
          ref={minuteRef}
          type="text"
          className="nv-time-minute"
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          placeholder="mm"
          aria-label={`${label} minutes`}
          value={draft.minute}
          onFocus={() => {
            focused.current = true;
          }}
          onBlur={leaveGroup}
          onChange={(e) => {
            const minute = ClockTime.sanitizeMinute(e.target.value);
            emit({ ...draft, minute });
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && draft.minute === "") {
              e.preventDefault();
              hourRef.current?.focus();
            }
          }}
        />
      </div>
    </FieldShell>
  );
}
